import { ReactNode, useMemo } from 'react'
import { Check, Circle } from 'lucide-react'
import { ActionButton } from './ActionButton'

export interface TimelineItem {
  id: string
  time: string
  content: ReactNode
  status?: 'pending' | 'completed' | 'missed'
  actions?: Array<{
    label: string
    onClick: () => void
    variant?: 'primary' | 'secondary'
    loading?: boolean
  }>
}

export interface TimelineGroup {
  time: string
  items: TimelineItem[]
}

export interface TimelineListProps {
  items: TimelineItem[]
  groupBy?: 'time' | 'none'
  emptyMessage?: string
  className?: string
}

function groupItemsByTime(items: TimelineItem[]): TimelineGroup[] {
  const groups: Record<string, TimelineItem[]> = {}

  for (const item of items) {
    const existing = groups[item.time]
    if (existing) {
      existing.push(item)
    } else {
      groups[item.time] = [item]
    }
  }

  return Object.entries(groups).map(([time, groupItems]) => ({
    time,
    items: groupItems,
  }))
}

function getStatusIcon(status: TimelineItem['status']) {
  switch (status) {
    case 'completed':
      return <Check size={16} className="timeline-item__icon timeline-item__icon--completed" />
    case 'missed':
      return <Circle size={16} className="timeline-item__icon timeline-item__icon--missed" />
    default:
      return <Circle size={16} className="timeline-item__icon timeline-item__icon--pending" />
  }
}

export function TimelineList({
  items,
  groupBy = 'time',
  emptyMessage = 'No hay elementos',
  className = '',
}: TimelineListProps) {
  const groups = useMemo(() => {
    if (groupBy === 'none') {
      return [{ time: '', items }]
    }
    return groupItemsByTime(items)
  }, [items, groupBy])

  if (items.length === 0) {
    return (
      <div className={`timeline-list timeline-list--empty ${className}`}>
        <p className="timeline-list__empty">{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className={`timeline-list ${className}`}>
      {groups.map((group, groupIndex) => (
        <div key={group.time || groupIndex} className="timeline-group">
          {group.time && (
            <div className="timeline-group__header">
              <span className="timeline-group__time">{group.time}</span>
            </div>
          )}
          <div className="timeline-group__items">
            {group.items.map((item) => (
              <div
                key={item.id}
                className={`timeline-item timeline-item--${item.status || 'pending'}`}
              >
                {getStatusIcon(item.status)}
                <div className="timeline-item__content">{item.content}</div>
                {item.actions && item.actions.length > 0 && (
                  <div className="timeline-item__actions">
                    {item.actions.map((action, actionIndex) => (
                      <ActionButton
                        key={actionIndex}
                        variant={action.variant || 'primary'}
                        size="sm"
                        onClick={action.onClick}
                        loading={action.loading}
                      >
                        {action.label}
                      </ActionButton>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
